import React, { useMemo } from 'react';
import { Box, Typography, Card, Grid, IconButton, LinearProgress, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Star,
  StarBorder,
  Public,
  Lock,
  DeleteForever,
  ErrorOutline,
  AccessTime,
} from '@mui/icons-material';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { en } from '../translations/en';
import { OptimizedImage } from './OptimizedImage';

export interface ImageGridImage {
  url: string;
  imageId?: string;
  visibility?: 'public' | 'private';
  uploadedAt?: string | null;
  // Processing status of the image. Accepts both backend-native values
  // ('READY', 'FAILED', 'PROCESSING', 'UPLOADING') and UploadContext-normalized
  // values ('completed', 'processing', 'failed', 'uploading', 'pending') so the
  // same grid renders correctly on the Upload tab and on ObjectDetails.
  status?: string;
  /** Original filename (e.g. "IMG_4821-front.jpg"). Shown under the thumbnail. */
  filename?: string;
  /** Pixel width of the original image. Pairs with `height` to render `WxH`. */
  width?: number;
  /** Pixel height of the original image. */
  height?: number;
  /** Original file size in bytes. Rendered as MB/KB under the dimensions. */
  fileSize?: number;
  /** Upload progress in 0..100 — drives the in-card overlay when status is 'uploading'. */
  progress?: number;
}

/** Format a byte count as a human-readable file size (e.g. "4.2 MB"). */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const UPLOADING_STATUSES = new Set(['uploading', 'UPLOADING', 'pending']);
const READY_STATUSES = new Set(['READY', 'completed']);
// Post-S3-upload, pre-rendition window. The Lambda image processor is still
// generating thumb/small/medium/large; the wizard tile has no image to show yet.
const PROCESSING_STATUSES = new Set(['processing', 'PROCESSING']);
// Polling cap hit without a terminal status — not a failure, just slower than
// the 5-minute budget. User can refresh later to see the eventual rendition.
const PROCESSING_TIMEOUT_STATUSES = new Set(['processing-timeout']);
// Hard failure from the server-side processor (decode error, OOM, etc).
const FAILED_STATUSES = new Set(['failed', 'FAILED']);

interface ImageGridProps {
  images: string[] | ImageGridImage[];
  selectable?: boolean;
  selectedIndex?: number | null;
  onImageSelect?: (index: number) => void;
  showVisibilityToggle?: boolean;
  onToggleVisibility?: (imageId: string) => void;
  showDeleteButton?: boolean;
  onDeleteImage?: (imageId: string) => void;
  /** Enables drag-to-reorder. Receives the new ordered array of imageIds. The
   * consumer is responsible for persisting the order; ImageGrid only emits. */
  onReorder?: (orderedImageIds: string[]) => void;
  /**
   * When set, suppresses the per-card star button and instead renders a
   * non-interactive "Primary" pill on the matching image. Used by upload
   * surfaces where leftmost = primary is enforced via drag-to-reorder, so
   * the explicit star promotion is redundant.
   */
  primaryImageId?: string | null;
  /**
   * Optional cell rendered as the last grid item — used by the edit-object
   * Upload Images flow for the mock's dashed "Add more" tile that triggers
   * the file picker. The cell is not draggable and is not included in
   * sortable contexts.
   */
  trailingCell?: React.ReactNode;
  'data-testid'?: string;
}

export const ImageGrid = ({
  images,
  selectable = false,
  selectedIndex,
  onImageSelect,
  showVisibilityToggle = false,
  onToggleVisibility,
  showDeleteButton = false,
  onDeleteImage,
  onReorder,
  primaryImageId,
  trailingCell,
  'data-testid': testId,
}: ImageGridProps) => {
  const theme = useTheme();

  // Normalize to a single shape for internal use
  const normalizedImages = useMemo<ImageGridImage[]>(
    () => images.map(img => (typeof img === 'string' ? { url: img } : img)),
    [images]
  );

  // Drag-to-reorder is opt-in: only enabled when onReorder is provided AND
  // every item has a stable imageId (we use the id as the dnd-kit `id`).
  const reorderEnabled = !!onReorder && normalizedImages.every(img => !!img.imageId);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!onReorder || !over || active.id === over.id) return;
    const ids = normalizedImages.map(img => img.imageId!);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  };

  if (normalizedImages.length === 0) return null;

  const grid = (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Grid container spacing={2} sx={{ width: '100%', maxWidth: selectable ? 800 : undefined, justifyContent: 'center' }}>
        {normalizedImages.map((image, index) => {
          const sortable = reorderEnabled && !!image.imageId;
          const cellInner = (
            <>
              <Card
                sx={{
                  position: 'relative',
                  width: '100%',
                  minHeight: 150,
                  maxHeight: 220,
                  cursor: selectable ? 'pointer' : 'default',
                  border: selectable && selectedIndex === index ? '4px solid' : 0,
                  borderColor: theme.palette.primary.main,
                  boxShadow: selectable && selectedIndex === index
                    ? `0 0 0 2px ${theme.palette.primary.light}, 0 4px 12px rgba(0,0,0,0.2)`
                    : undefined,
                  '&:hover': selectable ? { boxShadow: 3 } : {},
                  overflow: 'hidden',
                  backgroundColor: theme.palette.grey[100]
                }}
                onClick={selectable && onImageSelect ? () => onImageSelect(index) : undefined}
                data-testid={`image-${index}`}
              >
                <OptimizedImage
                  src={image.url}
                  alt={`Artwork ${index + 1}`}
                  loading="lazy"
                  sizes="(max-width: 600px) 50vw, (max-width: 960px) 33vw, 25vw"
                />
                {/* In-card upload progress overlay (mockup `.upl-progress`).
                    Renders only while the image is uploading; covers the thumbnail
                    with a translucent layer + bar + percent caption. */}
                {image.status && UPLOADING_STATUSES.has(image.status) && (
                  <Box
                    data-testid={`upload-progress-${image.imageId || index}`}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(255, 255, 255, 0.85)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      px: 2,
                    }}
                  >
                    <LinearProgress
                      variant={typeof image.progress === 'number' ? 'determinate' : 'indeterminate'}
                      value={typeof image.progress === 'number' ? image.progress : undefined}
                      sx={{ width: '72%', height: 4, borderRadius: 999 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Uploading{typeof image.progress === 'number' ? `… ${Math.round(image.progress)}%` : '…'}
                    </Typography>
                  </Box>
                )}
                {image.status && PROCESSING_STATUSES.has(image.status) && (
                  <Box
                    data-testid={`processing-overlay-${image.imageId || index}`}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(255, 255, 255, 0.85)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      px: 2,
                    }}
                  >
                    <CircularProgress size={28} />
                    <Typography variant="caption" color="text.secondary">
                      Processing…
                    </Typography>
                  </Box>
                )}
                {image.status && PROCESSING_TIMEOUT_STATUSES.has(image.status) && (
                  <Box
                    data-testid={`processing-timeout-overlay-${image.imageId || index}`}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      px: 2,
                      textAlign: 'center',
                    }}
                  >
                    <AccessTime fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      Still processing — refresh later
                    </Typography>
                  </Box>
                )}
                {image.status && FAILED_STATUSES.has(image.status) && (
                  <Box
                    data-testid={`processing-failed-overlay-${image.imageId || index}`}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      px: 2,
                      textAlign: 'center',
                    }}
                  >
                    <ErrorOutline fontSize="small" color="error" />
                    <Typography variant="caption" color="error">
                      Processing failed
                    </Typography>
                  </Box>
                )}
                {selectable && !primaryImageId && (
                  <IconButton
                    data-testid={
                      selectedIndex === index
                        ? `star-filled-${index}`
                        : `star-outline-${index}`
                    }
                    aria-label={
                      selectedIndex === index ? 'Primary image' : 'Make primary image'
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (selectedIndex !== index && onImageSelect) {
                        onImageSelect(index);
                      }
                    }}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      backgroundColor: 'rgba(255, 255, 255, 0.85)',
                      color:
                        selectedIndex === index
                          ? theme.palette.warning.main
                          : theme.palette.text.secondary,
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.95)' },
                      zIndex: 2,
                    }}
                  >
                    {selectedIndex === index ? (
                      <Star fontSize="small" />
                    ) : (
                      <StarBorder fontSize="small" />
                    )}
                  </IconButton>
                )}
                {primaryImageId && image.imageId === primaryImageId && (
                  <Box
                    data-testid={`primary-badge-${image.imageId}`}
                    aria-label="Primary image"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      px: 1,
                      py: 0.25,
                      borderRadius: 999,
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      zIndex: 2,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
                    }}
                  >
                    Primary
                  </Box>
                )}
                {showVisibilityToggle && image.imageId && onToggleVisibility && (
                  <IconButton
                    data-testid={`visibility-toggle-${image.imageId}`}
                    aria-label={image.visibility === 'public' ? 'Make image private' : 'Make image public'}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleVisibility(image.imageId!);
                    }}
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.95)' },
                    }}
                  >
                    {image.visibility === 'public' ? (
                      <Public fontSize="small" />
                    ) : (
                      <Lock fontSize="small" />
                    )}
                  </IconButton>
                )}
                {showDeleteButton && image.imageId && onDeleteImage && (
                  <IconButton
                    data-testid={`delete-image-${image.imageId}`}
                    aria-label="Delete image"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteImage(image.imageId!);
                    }}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        color: theme.palette.error.main,
                      },
                    }}
                  >
                    <DeleteForever fontSize="small" />
                  </IconButton>
                )}
              </Card>
              {/* Filename · dimensions · file-size caption (mockup `.img-meta`).
                  Renders only when the relevant metadata is available; missing
                  fields are silently skipped so legacy items without dims still
                  show what they have. */}
              {(image.filename || image.width || image.fileSize) && (
                <Box sx={{ mt: 0.5, px: 0.5 }}>
                  {image.filename && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={image.filename}
                    >
                      {image.filename}
                    </Typography>
                  )}
                  {(image.width || image.fileSize) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {[
                        image.width && image.height ? `${image.width} × ${image.height}` : null,
                        typeof image.fileSize === 'number' ? formatFileSize(image.fileSize) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  )}
                </Box>
              )}
              {image.uploadedAt && image.status && READY_STATUSES.has(image.status) && (
                <Box sx={{ mt: 0.5, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {new Date(image.uploadedAt).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </Typography>
                </Box>
              )}
            </>
          );
          // Grid item wraps the cell content directly. SortableCardWrapper
          // (a plain Box) goes INSIDE so the Grid container still sees Grid
          // items as its direct children — wrapping the Grid in a Box breaks
          // MUI's size class and collapses the cell to its content's
          // intrinsic width, which is visible whenever an unwrapped sibling
          // (like the trailing Add-more tile) shares the row.
          return (
            <Grid size={{ xs: 6, sm: 4, md: 3 }} key={image.imageId || index}>
              {sortable ? (
                <SortableCardWrapper id={image.imageId!}>
                  {cellInner}
                </SortableCardWrapper>
              ) : (
                cellInner
              )}
            </Grid>
          );
        })}
        {trailingCell && (
          <Grid size={{ xs: 6, sm: 4, md: 3 }} key="__trailing__">
            {trailingCell}
          </Grid>
        )}
        </Grid>
      </Box>
  );

  return (
    <Box sx={{ mt: 3 }} data-testid={testId} data-selectable={selectable}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {normalizedImages.length} {normalizedImages.length === 1 ? en.wizard.upload.addImages.image : en.wizard.upload.addImages.images}
        </Typography>
      </Box>

      {reorderEnabled ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={normalizedImages.map(img => img.imageId!)} strategy={rectSortingStrategy}>
            {grid}
          </SortableContext>
        </DndContext>
      ) : (
        grid
      )}
    </Box>
  );
};

/**
 * Wraps a card cell with dnd-kit sortable behavior. The entire card is draggable;
 * selection clicks pass through due to 5px motion threshold on PointerSensor.
 */
const SortableCardWrapper: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1 : 0,
        position: 'relative',
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </Box>
  );
};
